import { useState } from "react";
import { ArrowLeft, Bookmark, Columns2, Layers, RefreshCw, Sparkles, Wand2 } from "lucide-react";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";

export default function PreviewStep({
  photo,
  results,
  activeIdx,
  onSelectResult,
  usage,
  onTryAnotherStyle,
  onRegenerate,
  onLimit,
  onSave,
  onBack,
}) {
  const [mode, setMode] = useState("slider"); // slider | side
  const preview = results[activeIdx];
  // Total remaining across free + paid + lifetime credit buckets.
  const freeRem = usage?.freeRemaining ?? Math.max(0, (usage?.freeAllowed ?? 2) - (usage?.freeUsed ?? 0));
  const paidRem = usage?.paidCredits ?? 0;
  const lifeRem = usage?.lifetimeCredits ?? 0;
  const remaining = freeRem + paidRem + lifeRem;
  const remainingLabel = freeRem > 0
    ? `${freeRem} free preview${freeRem === 1 ? "" : "s"} remaining`
    : paidRem > 0
    ? `${paidRem} paid preview${paidRem === 1 ? "" : "s"} remaining`
    : lifeRem > 0
    ? `${lifeRem} lifetime preview${lifeRem === 1 ? "" : "s"} remaining`
    : "All previews used";

  const handleRegenerate = () => (remaining <= 0 ? onLimit() : onRegenerate());
  const handleAnother = () => (remaining <= 0 ? onLimit() : onTryAnotherStyle());

  return (
    <div className="max-w-3xl mx-auto px-4 pb-24" data-testid="preview-step">
      <div className="text-center fade-up">
        <span className="text-xs font-bold tracking-widest uppercase text-[#c84125]">Step 3 · Your preview</span>
        <h1 className="font-display mt-3 text-3xl sm:text-4xl font-extrabold text-[#0f172a] tracking-tight">
          {preview.style?.name ? `Here's the ${preview.style.name} look.` : "Here's your new look."}
        </h1>
        <p className="mt-3 text-sm sm:text-base text-[#475569]">
          Same dog, new style. Drag to compare before and after.
        </p>
      </div>

      {/* Hero: AI result */}
      <div className="mt-8 fade-up delay-1">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div className="inline-flex rounded-full bg-white border border-[#eae4d9] p-1 shadow-sm">
            <ToggleBtn active={mode === "slider"} onClick={() => setMode("slider")} testId="compare-mode-slider">
              <Layers className="w-3.5 h-3.5" /> Slider
            </ToggleBtn>
            <ToggleBtn active={mode === "side"} onClick={() => setMode("side")} testId="compare-mode-side">
              <Columns2 className="w-3.5 h-3.5" /> Side by side
            </ToggleBtn>
          </div>
          <span
            data-testid="previews-remaining"
            className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold border ${
              remaining > 0
                ? "bg-[#fff7ed] text-[#b45309] border-[#fde7c7]"
                : "bg-[#ffe8e0] text-[#c84125] border-[#ffd8cf]"
            }`}
          >
            <Sparkles className="w-3.5 h-3.5" />
            {remainingLabel}
          </span>
        </div>

        {mode === "slider" ? (
          <div className="rounded-3xl overflow-hidden border-2 border-[#eae4d9] bg-white shadow-[0_18px_45px_-12px_rgba(15,23,42,0.22)]">
            <BeforeAfterSlider
              beforeSrc={photo.dataUrl}
              afterSrc={preview.previewImage}
              afterLabel={preview.isMock ? "PREVIEW MODE" : "AI PREVIEW"}
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Figure src={photo.dataUrl} label="BEFORE" testId="preview-before-img" muted />
            <Figure
              src={preview.previewImage}
              label={preview.isMock ? "PREVIEW MODE" : `AFTER · ${(preview.style?.name || "AI PREVIEW").toUpperCase()}`}
              testId="preview-after-img"
            />
          </div>
        )}

        {preview.isMock && (
          <p data-testid="mock-notice" className="mt-3 text-xs text-[#b45309] bg-[#fff7ed] border border-[#fde7c7] rounded-xl px-3 py-2">
            Preview mode: the AI provider isn't configured on this server, so this is a placeholder of your original photo.
          </p>
        )}
      </div>

      {/* Compare styles generated this session */}
      {results.length > 1 && (
        <div className="mt-6 fade-up delay-2">
          <div className="text-xs font-bold tracking-widest uppercase text-[#64748b] mb-2">Compare your styles</div>
          <div className="flex gap-3 overflow-x-auto pb-1" data-testid="results-strip">
            {results.map((r, i) => (
              <button
                key={r.id}
                type="button"
                data-testid={`result-thumb-${i}`}
                onClick={() => onSelectResult(i)}
                className={`shrink-0 w-28 rounded-2xl overflow-hidden border-2 bg-white text-left transition-all ${
                  i === activeIdx ? "border-[#ff6b4a] shadow-md" : "border-[#eae4d9] hover:border-[#d6cfc2]"
                }`}
              >
                <div className="aspect-square bg-[#f3efe6]">
                  <img src={r.previewImage} alt={r.style?.name} className="w-full h-full object-cover" />
                </div>
                <div className="px-2 py-1.5 text-[11px] font-semibold text-[#0f172a] truncate">{r.style?.name || "Custom"}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Actions — one primary */}
      <div className="mt-8 space-y-3 fade-up delay-2">
        <button
          data-testid="looks-good-btn"
          onClick={onSave}
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#ff6b4a] hover:bg-[#e85535] text-white font-semibold px-7 py-3.5 shadow-[0_8px_25px_-4px_rgba(255,107,74,0.35)] hover:shadow-[0_12px_30px_-4px_rgba(255,107,74,0.45)] transition-all duration-200 active:scale-[0.98]"
        >
          <Bookmark className="w-4 h-4" /> Save & Create Groomer Card
        </button>
        <div className="grid grid-cols-2 gap-3">
          <button
            data-testid="try-another-style-btn"
            onClick={handleAnother}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-white hover:bg-[#f3efe6] text-[#0f172a] font-semibold px-5 py-3 border border-[#eae4d9] shadow-sm transition-all duration-200 active:scale-[0.98]"
          >
            <Wand2 className="w-4 h-4" /> Try another style
          </button>
          <button
            data-testid="regenerate-btn"
            onClick={handleRegenerate}
            className={`inline-flex items-center justify-center gap-2 rounded-full font-semibold px-5 py-3 border shadow-sm transition-all duration-200 active:scale-[0.98] ${
              remaining <= 0
                ? "bg-[#f3efe6] text-[#94a3b8] border-[#eae4d9]"
                : "bg-white hover:bg-[#f3efe6] text-[#0f172a] border-[#eae4d9]"
            }`}
          >
            <RefreshCw className="w-4 h-4" /> Generate again
          </button>
        </div>
        <button
          data-testid="preview-back-btn"
          onClick={onBack}
          className="w-full inline-flex items-center justify-center gap-1.5 text-sm text-[#64748b] hover:text-[#0f172a] py-2 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to styles
        </button>
      </div>

      <p data-testid="preview-disclaimer" className="mt-6 text-xs text-[#64748b] text-center max-w-xl mx-auto leading-relaxed">
        AI previews are visual references only. Your groomer will determine the final cut based on your dog's coat,
        condition, breed, and grooming requirements.
      </p>
    </div>
  );
}

const ToggleBtn = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
      active ? "bg-[#0f172a] text-white" : "text-[#475569] hover:text-[#0f172a]"
    }`}
  >
    {children}
  </button>
);

const Figure = ({ src, label, testId, muted }) => (
  <figure className="rounded-3xl overflow-hidden border border-[#eae4d9] bg-white shadow-[0_4px_20px_-2px_rgba(15,23,42,0.06)]">
    <div className="aspect-square bg-[#f3efe6]">
      <img src={src} alt={label} className="w-full h-full object-cover" data-testid={testId} />
    </div>
    <figcaption className={`px-4 py-3 text-[11px] font-bold tracking-[0.15em] ${muted ? "text-[#64748b]" : "text-[#c84125]"}`}>
      {label}
    </figcaption>
  </figure>
);
